import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Button, IconButton } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectInventorySelections } from '@/redux/selectors/orderPlacementSelectors';
import { removeInventorySelection } from '@/redux/slices/orderPlacementSlice';
import { FileTypeDisplay } from '@/exports/columnCommonComponents';
import VideoPlayerModal from '../v2/TitleDetailInventoryV2/VideoPlayerModal';
import { selectAllLanguages } from '@/redux/selectors/constantsSelectors';
import { InventoryFileEntity } from '@/redux/slices/slice_types';
import useFileType from '@/components/_hooks/useFileType';

interface FileSelectProps {
  rootTitleId: number;
}

const FileSelect = ({ rootTitleId }: FileSelectProps) => {
  const dispatch = useAppDispatch();
  const selected = useAppSelector(selectInventorySelections);
  const languages = useAppSelector(selectAllLanguages);
  const [showVideoPlayer, setShowVideoPlayer] = React.useState(false);
  const getFileGroup = useFileType();

  const groupedBySection = useMemo(() => {
    return selected.reduce(
      (acc, file) => {
        const section = file.file_category || getFileGroup(file.file_type) || 'Uncategorized';
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(file);
        return acc;
      },
      {} as Record<string, typeof selected>
    );
  }, [selected, getFileGroup]);

  const sections = Object.keys(groupedBySection);

  const removeFile = (fileId: number) => {
    const file = selected.find(({ id }) => id === fileId);

    if (file) {
      dispatch(removeInventorySelection(file));
    }
  };

  const getFileLanguage = (file: InventoryFileEntity) => {
    let audioLang = undefined;
    if (file.label) {
      const match = file.label.match(/Audio-([^_]+)/);
      if (match && match[1]) {
        audioLang = match[1].replace('-', '_').toLowerCase();
      }
    }
    const match = languages.find((lang) =>
      file.rally_asset_tags.some((tag) => {
        return (
          lang.code?.toLowerCase() === audioLang ||
          lang.label?.toLowerCase() === tag.toLowerCase() ||
          lang.code?.toLowerCase() === tag.replace('lang:', '').replace('-', '_').toLowerCase()
        );
      })
    );
    return match?.label || 'Unknown';
  };

  const renderGroupedFiles = () => {
    return sections.map((section) => (
      <Box sx={{ mb: 2 }} key={section}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontWeight: 600, fontSize: '1rem' }}>
          <GroupedFileType chip={false} type={section} sx={{ mb: 1.5 }} />({groupedBySection[section].length})
        </Box>
        {groupedBySection[section].map((file) => {
          const fileName = file.name?.split('/').pop() || file.label;

          return (
            <SelectedFileRow key={file.id}>
              <SelectedFileMeta>
                <SelectedFileLanguage>{getFileLanguage(file)}</SelectedFileLanguage>
                <SelectedFileName>{fileName}</SelectedFileName>
              </SelectedFileMeta>
              <RemoveFileButton size="small" onClick={() => removeFile(file.id)}>
                <PolarisIcon icon="close" width={14} height={14} />
              </RemoveFileButton>
            </SelectedFileRow>
          );
        })}
      </Box>
    ));
  };

  const onLaunchPlayer = () => {
    setShowVideoPlayer(true);
  };

  return (
    <>
      <VideoPlayerModal rootTitleId={rootTitleId} files={selected} onClose={() => setShowVideoPlayer(false)} open={showVideoPlayer} />
      <StyledAccordion>
        <AccordionSummary expandIcon={<PolarisIcon width={17} height={17} icon="maximize" />} aria-controls="panel1-content" id="panel1-header">
          <Typography component="span">Media Selected</Typography>
          <Box sx={{ marginLeft: 'auto', marginRight: 1, display: 'flex', alignItems: 'center' }}>
            <PolarisIcon width={21} height={21} icon="home_storage" />
            {!!selected.length && <Count>{selected.length}</Count>}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <ContentWrapper>
            {!selected.length || !groupedBySection['video']?.length ? (
              <EmptyState>
                <PolarisIcon icon="home_storage" width={40} height={40} />
                <Typography variant="body2">{!selected.length ? 'Select media files to start' : 'No video file selected'}</Typography>
              </EmptyState>
            ) : null}
            {renderGroupedFiles()}
            {!!groupedBySection['video'] && (
              <Button sx={{ ml: 'auto' }} variant="contained" color="error" onClick={onLaunchPlayer}>
                Launch Player
              </Button>
            )}
          </ContentWrapper>
        </AccordionDetails>
      </StyledAccordion>
    </>
  );
};

export default FileSelect;

const EXPANDED_WIDTH = 283;

const StyledAccordion = styled(Accordion)`
  position: relative;
  width: 220px;
  height: 38px;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.11);
  transition: box-shadow 150ms ease;
  border-top: 2px solid #9fe6dd;
  padding: ${({ theme }) => theme.spacing(0, 1.5)};

  &.Mui-expanded {
    width: ${EXPANDED_WIDTH}px;
  }

  .MuiAccordionSummary-root {
    width: 100%;
    height: 100%;
    flex-direction: row;
  }

  && .Mui-expanded {
    margin: 0;
    min-height: unset;
  }

  .MuiCollapse-root {
    position: absolute;
    width: 100%;
    z-index: 10;
    margin-left: ${({ theme }) => theme.spacing(-1.5)};
  }

  .MuiAccordionDetails-root {
    padding: 0;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: ${EXPANDED_WIDTH}px;
  padding: ${({ theme }) => theme.spacing(1.5)};
  max-height: 520px;
  overflow-y: auto;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0 0 ${({ theme }) => theme.shape.borderRadius}px ${({ theme }) => theme.shape.borderRadius}px;
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease;
`;

const Count = styled.div`
  width: 21px;
  height: 21px;
  background: #ff7062;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.85rem;
  font-weight: 700;
`;

const EmptyState = styled.div`
  min-height: 96px;
  padding: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.text.secondary};
  border: 1px dashed ${({ theme }) => theme.palette.divider};
`;

const SelectedFileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1.25, 2)};
  margin: ${({ theme }) => theme.spacing(1, 0)};
  background: ${({ theme }) => theme?.palette.background.default + '6a'};

  &:not(:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }
`;

const SelectedFileMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.25)};
`;

const SelectedFileLanguage = styled.div`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedFileName = styled.div`
  text-transform: capitalize;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 0.85rem;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const GroupedFileType = styled(FileTypeDisplay)`
  display: inline-flex;

  svg {
    color: ${({ theme }) => theme.palette.text.secondary};
    width: 14px;
    height: 14px;
    box-sizing: content-box;
  }

  span {
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 1rem;
  }
`;

const RemoveFileButton = styled(IconButton)`
  color: ${({ theme }) => theme.palette.text.primary};
`;
